import { useMemo, useState } from 'react'
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Form,
  Input,
  Popover,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { resetTotp, type Scope } from '@/shared/auth/authApi'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import {
  CURRENCY_CODES,
  SUGGESTED_CURRENCIES,
  looksLikeCurrencyCode,
  normalizeCurrencyCode,
} from '@/shared/lib/currencies'
import {
  browserTimeZone,
  currencyName,
  currencySymbol,
  formatDateTimeInZone,
  getDisplayTimeZone,
  localZoneLabel,
  setCurrencyCode,
  setDisplayTimeZone,
  zoneOffsetLabel,
} from '@/shared/lib/format'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { shortId } from '@/shared/lib/shortId'
import { SelfPasswordCard } from '@/features/identity/SelfPasswordCard'
import { ContractTemplateCard } from '@/features/contract/ContractTemplateCard'
import { FormSettingsCard } from '@/features/subscriber/FormSettingsCard'
import { MenuModulesCard } from '@/features/menu/MenuModulesCard'
// ⚠️ `features/*` bir-birini import qilmaydi degan qoidaning shu yerdagi
// istisnosi `UsersPage` bilan bir xil: tuzilma daraxti YAGONA egada
// (`features/org`) va uni ikkinchi marta qurish ikkinchi haqiqat manbai
// bo'lardi (`useOrgUnits` javadoc'i).
import { useOrgUnits, type OrgUnitsApi } from '@/features/identity/useOrgUnits'
import { TENANT_LOCALES, timeZoneOptions } from '@/features/tenants/timeZones'
import { PageHeader } from '@/shared/ui/PageHeader'

/** Namuna summa — valyuta qanday ko'rinishini ko'rsatadi */
const SAMPLE_MINOR = 5_000_000

/**
 * Uzun tushuntirish uchun YORDAM belgisi — BUG-045.
 *
 * <h2>Nima o'lchangan</h2>
 * 2026-08-16, jonli panel, `/settings`: 13 ta matn bloki, jami
 * <b>978 belgi</b>, ulardan 4 tasi 120 belgidan uzun (eng uzuni 220).
 * Sahifadagi butun matn 1444 belgi edi — ya'ni ekranning ~68% i
 * tushuntirish, boshqaruvlar esa ular orasida yo'qolib ketardi.
 * Tester izohi: «TEXTLARNI KAMAYTIRISH KK».
 *
 * <h2>⚠️ MATN O'CHIRILMADI, KO'CHIRILDI</h2>
 * Uzun matnlarning har biri KERAKLI faktni tashiydi (nega valyuta
 * qotib qoladi, nega zona hisobotlarga ta'sir qiladi). Ularni
 * qisqartirib tashlash — nuqsonni boshqa tomondan qaytarish bo'lardi:
 * operator sababini bilmay qolardi. Shuning uchun qisqa jumla
 * ko'rinib turadi, to'lig'i esa shu belgining ortida.
 *
 * ⚠️ `Popover`, `Tooltip` EMAS: matn bir necha jumla va uni O'QISH
 * kerak — sichqonchani olib qo'yishi bilan yopiladigan qalqib chiquvchi
 * yozuvda uni o'qib bo'lmasdi. `trigger="click"` bilan u ochiq qoladi.
 */
function HelpDot({ title, text }: { title: string; text: string }) {
  return (
    <Popover
      trigger="click"
      title={title}
      content={<div style={{ maxWidth: 360 }}>{text}</div>}
    >
      <Button type="text" size="small" icon={<QuestionCircleOutlined />} aria-label={title} />
    </Popover>
  )
}

/**
 * Bitta ko'rinish doirasi — TURI va (bo'lsa) NISHONI.
 *
 * <h2>⚠️ `scope.id` NEGA KO'RSATILADI</h2>
 * `SUBTREE` doirali xodim uchun bu yagona javob beradigan qiymat:
 * «men qaysi shoxni ko'raman». Usiz ro'yxatlar qisqarib turadi va
 * sababi ekranda umuman ko'rinmaydi — operator buni nosozlik deb
 * o'qirdi.
 *
 * <h2>⚠️ NOMI TOPILMASA — ID, BO'SH JOY EMAS</h2>
 * Uchta sabab bo'lishi mumkin va ular bir-biridan farq qilmaydi:
 * `org.node.read` yo'q, tugun doiradan tashqarida, yoki bu umuman
 * boshqa reyestrning identifikatori (`VENUE` doirasi `venue.venue`
 * ga ishora qiladi). Uchalasida ham qisqartirilgan id chiziladi va
 * sabab izohda aytiladi: bo'sh katak «biriktirilmagan» degan YOLG'ON
 * berardi. Bu `UsersPage` dagi `partyNode` naqshining aynan o'zi.
 */
function ScopeTag({ scope, orgUnits }: { scope: Scope; orgUnits: OrgUnitsApi }) {
  const { t } = useTranslation()
  const label = t(`identity.scope.${scope.type}`, { defaultValue: scope.type })

  if (scope.id === null) return <Tag>{label}</Tag>

  const path = orgUnits.pathOf(scope.id)
  if (path !== null) {
    return (
      <Tag>
        {label} · {path}
      </Tag>
    )
  }

  return (
    <Tooltip title={t('settings.tenant.scopeIdHint')}>
      <Tag>
        {label} · <Typography.Text code>{shortId(scope.id)}</Typography.Text>
      </Tag>
    </Tooltip>
  )
}

/**
 * `GET/PUT /api/tenant-settings` javobi.
 *
 * Faqat KO'RSATISH uchun ishlatiladigan maydonlar (`businessKind`, `state`,
 * `currency`) ataylab yumshoq tiplangan: ulardan biri kelmasa ham sozlamalar
 * kartasi ochilishi va zona tahrirlanishi kerak.
 */
const tenantSettingsSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  businessKind: z.enum(['ISP', 'VENUE']).nullish(),
  currency: z.string().nullish(),
  /**
   * ⚠️ ENDI HAQIQIY HISOBLANGAN QIYMAT (ilgari server doim `false`
   * berardi). Backend uni `ledger.txn` va narx qo'yilgan
   * `catalog.plan` dan hisoblaydi.
   */
  currencyEditable: z.boolean().default(false),
  /**
   * Nega tayyor sabab MATNI emas, SONLAR: panel uch tilda ishlaydi,
   * serverda yasalgan jumla esa ularning bittasida qolib ketardi.
   * Eski backend bu maydonlarni bermaydi — shuning uchun `default(0)`.
   */
  currencyLockTxnCount: z.number().default(0),
  currencyLockPricedPlanCount: z.number().default(0),
  dayTimezone: z.string(),
  locale: z.string(),
  state: z.string().nullish(),
})

type TenantSettings = z.infer<typeof tenantSettingsSchema>

interface TenantSettingsDraft {
  name: string
  dayTimezone: string
  locale: string
  /** ⚠️ Yuborilmasa backend valyutaga TEGMAYDI (`TenantSettingsUpdate` javadoc'i) */
  currency?: string
}

async function fetchTenantSettings(): Promise<TenantSettings> {
  const response = await api.get<unknown>('/api/tenant-settings')
  return tenantSettingsSchema.parse(response.data)
}

async function updateTenantSettings(draft: TenantSettingsDraft): Promise<TenantSettings> {
  const response = await api.put<unknown>('/api/tenant-settings', draft)
  return tenantSettingsSchema.parse(response.data)
}

export function SettingsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { can } = usePermissions()
  /*
    ⚠️ BUG-048 — `modal` AYNAN SHU YERDAN, `Modal.confirm` STATIK
    chaqiruvidan EMAS.

    O'lchangan (2026-08-16, jonli panel, qorong'i mavzu yoqilgan holda):
      statik `Modal.confirm`  → oyna foni rgb(255,255,255), matn rgba(0,0,0,.88)
      `App.useApp().modal`    → oyna foni rgb(22,31,51),   matn rgb(226,232,240)
      ilova foni ikkalasida ham rgb(8,13,24)

    Sabab: statik `Modal.confirm` React daraxtidan TASHQARIDA yangi ildiz
    yasaydi va `ConfigProvider` ning mavzu kontekstiga umuman yetmaydi —
    ya'ni qorong'i panel ustida OQ dialog chiqadi. `App.useApp()` bergan
    nusxa esa kontekst ichida qoladi. Tester izohi: «POPUP RANGI OQ,
    BACKGROUND ESA QORA, BIR BIRIGA TUSHMAYABDI».
  */
  const { message, modal } = App.useApp()
  const queryClient = useQueryClient()

  const [resettingTotp, setResettingTotp] = useState(false)

  // ⚠️ Bu QULAYLIK, xavfsizlik EMAS — backend har so'rovda huquqni qayta
  // tekshiradi (`settings.read` / `settings.update`). Bu yerda yashirishning
  // yagona maqsadi — 403 bilan tugaydigan tugma ko'rsatmaslik.
  const canRead = can('settings.read')
  const canUpdate = can('settings.update')

  /**
   * TOTP'ni qayta o'rnatish — xavfli amal: keyingi kirishda foydalanuvchi
   * QR'ni qaytadan skanerlashi kerak bo'ladi (`TOTP_SETUP` bosqichi qayta
   * ishga tushadi). Shuning uchun tasdiqlash modali orqali o'tadi.
   */
  const confirmResetTotp = () => {
    if (!user) return
    modal.confirm({
      title: t('auth.totp.resetConfirmTitle'),
      content: t('auth.totp.resetConfirmText'),
      okButtonProps: { danger: true },
      okText: t('auth.totp.resetButton'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        setResettingTotp(true)
        try {
          await resetTotp(user.id)
          void message.success(t('auth.totp.resetDone'))
        } catch (error) {
          void message.error(t('errors.generic'))
          throw error
        } finally {
          setResettingTotp(false)
        }
      },
    })
  }

  const settings = useQuery({
    queryKey: queryKeys.tenantSettings,
    queryFn: fetchTenantSettings,
    enabled: canRead,
  })

  /*
    ⚠️ Tuzilma daraxti FAQAT kerak bo'lganda so'raladi: id li doira
    yo'q bo'lsa (`GLOBAL`, `TENANT`, `OWN`) tarjima qiladigan narsa
    ham yo'q. Kesh kaliti «Tuzilma» sahifasiniki, ya'ni u ochilgan
    bo'lsa ikkinchi so'rov umuman ketmaydi (`useOrgUnits` javadoc'i).
  */
  const orgUnits = useOrgUnits((user?.scopes ?? []).some((scope) => scope.id !== null))

  // Valyuta serverdan: sozlamalar endpointi ochilmagan bo'lsa (`settings.read`
  // yo'q) `me` dagi qiymat ishlatiladi — ikkalasi ham bir manbadan keladi.
  const currency = settings.data?.currency ?? user?.tenant.currency ?? 'UZS'
  const displayZone = getDisplayTimeZone()
  const browserZone = browserTimeZone()

  return (
    <>
      <PageHeader title={t('nav.settings')} description={t('settings.intro')} />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={t('settings.tenant.title')}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={t('settings.tenant.id')}>
                <Typography.Text code>{user?.tenant.id ?? '—'}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('settings.tenant.name')}>
                {settings.data?.name ?? user?.tenant.name ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('auth.username')}>
                {user?.displayName ?? '—'}
              </Descriptions.Item>
              {/*
                ⚠️ TUR — lug'atdan, `scope.id` esa OCHIQ ko'rsatiladi.

                Ilgari bu yerda xom `scope.type` (`GLOBAL`, `SUBTREE`)
                chizilardi, `scope.id` esa UMUMAN chizilmasdi. Ikkinchisi
                og'irroq nuqson edi: `SUBTREE` doirali xodim «men bir
                shoxni ko'raman» deganini bilardi-yu, QAYSI shox ekanini
                bilmasdi — ya'ni ro'yxat nega qisqa ekanini tushuntirib
                beradigan yagona ma'lumot yashirin edi.

                ⚠️ Nomi `useOrgUnits` dan: `SUBTREE.scope_id` —
                `platform.org_node` (`ResellerScope`, `PersonScopeSql`).
                So'rov FAQAT id bor doira mavjud bo'lganda ketadi:
                `GLOBAL`/`TENANT` da id yo'q va butun daraxtni tortish
                sof isrof bo'lardi.
              */}
              <Descriptions.Item label={t('settings.tenant.scopes')}>
                <Space wrap>
                  {(user?.scopes ?? []).map((scope) => (
                    <ScopeTag key={`${scope.type}-${scope.id ?? ''}`} scope={scope} orgUnits={orgUnits} />
                  ))}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('settings.tenant.permissions')}>
                {user?.permissions.size ?? 0}
              </Descriptions.Item>
              {/* IKKALA zona ham ko'rsatiladi: panel tenant zonasida yozadi,
                  xodimning soati esa brauzer zonasida yuradi. Farqni ko'rmasa
                  operator "soat noto'g'ri" deb o'ylaydi. */}
              <Descriptions.Item label={t('timezone.tenant')}>
                {displayZone} · {zoneOffsetLabel(displayZone)}
              </Descriptions.Item>
              <Descriptions.Item label={t('timezone.browser')}>
                {browserZone} · {localZoneLabel()}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t('auth.totp.sectionTitle')}>
            <Typography.Paragraph type="secondary">
              {t('auth.totp.sectionDescription')}
            </Typography.Paragraph>
            <Button danger loading={resettingTotp} onClick={confirmResetTotp} disabled={!user}>
              {t('auth.totp.resetButton')}
            </Button>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          {/* ⚠️ BUG-046 — o'z parolini almashtirish. Sahifada ilgari
              faqat «TOTP ni qayta o'rnatish» bor edi; parolga oid
              bironta boshqaruv yo'q edi (`SelfPasswordCard` javadoc'i). */}
          <SelfPasswordCard />
        </Col>

        {canUpdate ? (
          <Col xs={24} lg={12}>
            <MenuModulesCard />
          </Col>
        ) : null}

        {canUpdate && can('subscriber.contract.view') ? (
          <Col xs={24} lg={12}>
            <ContractTemplateCard />
          </Col>
        ) : null}

        {canUpdate && can('subscriber.read') ? (
          <Col xs={24} lg={12}>
            <FormSettingsCard />
          </Col>
        ) : null}

        <Col xs={24} lg={12}>
          <Card
            title={t('settings.tenant.editTitle')}
            extra={<HelpDot title={t('settings.help')} text={t('settings.tenant.editHelp')} />}
          >
            <Typography.Paragraph type="secondary">
              {t('settings.tenant.editDescription')}
            </Typography.Paragraph>

            {!canRead ? (
              <Alert type="info" showIcon message={t('errors.forbidden')} />
            ) : settings.data ? (
              <TenantSettingsForm
                // `key` — server qiymati kelgach forma QAYTA yasaladi.
                // `useEffect` bilan sinxronlashdan ko'ra ishonchli: oraliq
                // holat umuman bo'lmaydi.
                key={`${settings.data.id}-${settings.data.dayTimezone}-${settings.data.locale}`}
                settings={settings.data}
                editable={canUpdate}
                onSaved={(saved) => {
                  // Zona modul darajasida saqlanadi va REAKTIV EMAS: darhol
                  // o'rnatiladi, so'ng `me` keshi bekor qilinadi — shundagina
                  // allaqachon chizilgan sahifalar yangi zonada qayta
                  // chiziladi (valyuta oqimidagi bilan bir xil sabab).
                  setDisplayTimeZone(saved.dayTimezone)
                  void queryClient.invalidateQueries({ queryKey: queryKeys.me })
                  void queryClient.invalidateQueries({ queryKey: queryKeys.tenantSettings })
                  void message.success(t('settings.tenant.saved'))
                }}
                onError={(text) => void message.error(text)}
              />
            ) : (
              <Typography.Text type="secondary">
                {settings.isError ? translateError(settings.error, t) : t('common.loading')}
              </Typography.Text>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={t('settings.currency.title')}
            extra={<HelpDot title={t('settings.help')} text={t('settings.currency.help')} />}
          >
            <Typography.Paragraph type="secondary">
              {t('settings.currency.description')}
            </Typography.Paragraph>

            {/*
              ⚠️ VALYUTA — SHARTLI tahrirlanadi, va shart moliyaviy.
              `ledger.entry` / `ledger.txn` bazada O'ZGARMAS
              (`DO INSTEAD NOTHING` qoidasi va `reject_mutation()`
              triggeri), ya'ni eski yozuvlar eski valyutada QOLADI va
              qayta hisoblanmaydi. Shuning uchun almashtirish faqat
              tashkilotda hali bironta tranzaksiya va narx qo'yilgan
              reja yo'q paytda mumkin — bu amalda yangi tashkilot
              sozlanayotgan payt.

              Ruxsatni panel O'ZI TAXMIN QILMAYDI: `currencyEditable`
              backenddan keladi va u yerda qayta tekshiriladi.
            */}
            {settings.data ? (
              <CurrencyCardBody
                key={`${settings.data.id}-${settings.data.currency ?? ''}`}
                settings={settings.data}
                editable={canUpdate && settings.data.currencyEditable}
                onSaved={(saved) => {
                  // Valyuta ham zona kabi modul darajasida saqlanadi va
                  // REAKTIV EMAS — darhol o'rnatiladi, so'ng `me` keshi
                  // bekor qilinadi
                  setCurrencyCode(saved.currency ?? null)
                  void queryClient.invalidateQueries({ queryKey: queryKeys.me })
                  void queryClient.invalidateQueries({ queryKey: queryKeys.tenantSettings })
                  void message.success(t('settings.currency.saved'))
                }}
                onError={(text) => void message.error(text)}
              />
            ) : (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={t('settings.currency.current')}>
                  <Space>
                    <Typography.Text code>{currency}</Typography.Text>
                    <Typography.Text>{currencyName(currency)}</Typography.Text>
                    <Tag>{currencySymbol(currency)}</Tag>
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            )}

            <Divider style={{ margin: '12px 0' }} />

            <Statistic
              title={t('settings.currency.sample')}
              value={formatMoneyValue(money(SAMPLE_MINOR, currency))}
            />
          </Card>
        </Col>
      </Row>
    </>
  )
}

interface CurrencyCardBodyProps {
  settings: TenantSettings
  /** `settings.update` huquqi VA backend bergan `currencyEditable` — ikkalasi ham shart */
  editable: boolean
  onSaved: (saved: TenantSettings) => void
  onError: (text: string) => void
}

/**
 * Valyuta maydoni — ko'rinadi, lekin har doim ham tahrirlanmaydi.
 *
 * ⚠️ Yopiq bo'lsa SABABI yoziladi, "ruxsat yo'q" emas: operator
 * nimadan iborat to'siqni ko'rmasa, uni yo'qotib bo'lmaydigan xato deb
 * qabul qiladi va yordam xizmatiga chiqadi. Son (`N ta tranzaksiya`)
 * esa o'zi tushuntiradi.
 */
function CurrencyCardBody({ settings, editable, onSaved, onError }: CurrencyCardBodyProps) {
  const { t } = useTranslation()
  // ⚠️ BUG-048 — `modal` KONTEKSTDAN: statik `Modal.confirm` `ConfigProvider`
  // mavzusiga yetmaydi va qorong'i panel ustida OQ dialog chizadi.
  const { modal } = App.useApp()
  const current = settings.currency ?? 'UZS'
  const [selected, setSelected] = useState(current)

  const save = useMutation({
    mutationFn: updateTenantSettings,
    onSuccess: onSaved,
    onError: (error) => onError(translateError(error, t)),
  })

  // Ro'yxat qisqa (`currencies.ts`), lekin qidiruv bilan ISTALGAN ISO 4217
  // kodini yozib yuborish mumkin — backend uni `java.util.Currency` bo'yicha
  // tekshiradi, ya'ni ro'yxat cheklov emas, qulaylik
  const options = useMemo(() => {
    const codes = [...SUGGESTED_CURRENCIES, ...CURRENCY_CODES]
    if (!codes.includes(current)) codes.unshift(current)
    return Array.from(new Set(codes)).map((code) => ({
      value: code,
      label: `${code} · ${currencyName(code)}`,
    }))
  }, [current])

  return (
    <>
      {!editable ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('settings.currency.lockedTitle')}
          description={
            settings.currencyLockTxnCount > 0 || settings.currencyLockPricedPlanCount > 0
              ? t('settings.currency.lockedByHistory', {
                  txns: settings.currencyLockTxnCount,
                  plans: settings.currencyLockPricedPlanCount,
                })
              : t('settings.currency.lockedNoPermission')
          }
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('settings.currency.editableTitle')}
          description={t('settings.currency.editableHint')}
        />
      )}

      <Descriptions column={1} size="small" bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label={t('settings.currency.current')}>
          <Space>
            <Typography.Text code>{current}</Typography.Text>
            <Typography.Text>{currencyName(current)}</Typography.Text>
            <Tag>{currencySymbol(current)}</Tag>
          </Space>
        </Descriptions.Item>
      </Descriptions>

      <Space.Compact style={{ width: '100%' }}>
        <Select
          showSearch
          disabled={!editable || save.isPending}
          value={selected}
          options={options}
          optionFilterProp="label"
          style={{ flex: 1 }}
          onChange={setSelected}
          // Ro'yxatda yo'q kodni ham qabul qilamiz — `looksLikeCurrencyCode`
          // faqat shaklni tekshiradi, haqiqiyligini backend aytadi
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          notFoundContent={
            looksLikeCurrencyCode(selected) ? null : t('settings.tenant.zoneNoMatch')
          }
        />
        {/*
          ⚠️ TASDIQ MAJBURIY va matn AYNAN NIMA BO'LISHINI aytadi.
          Amal qaytarib bo'lmaydi: `ledger.entry`/`ledger.txn` bazada
          o'zgarmas (`reject_mutation()` triggeri), ya'ni mavjud yozuvlar
          eski valyutada QOLADI va qayta hisoblanmaydi. Umumiy «Davom
          etasizmi?» bu yerda foydasiz — operator eski summalar nima
          bo'lishini bilishi kerak, shuning uchun matnda ikkala kod ham
          bor. Tugma ham `danger`: u shu kartadagi yagona qaytarilmaydigan
          amal.
        */}
        <Button
          type="primary"
          danger
          disabled={!editable || selected === current}
          loading={save.isPending}
          onClick={() => {
            const next = normalizeCurrencyCode(selected)
            modal.confirm({
              title: t('settings.currency.confirmTitle'),
              content: t('settings.currency.confirmBody', { from: current, to: next }),
              okText: t('settings.currency.confirmOk'),
              okButtonProps: { danger: true },
              cancelText: t('common.cancel'),
              onOk: () =>
                save
                  .mutateAsync({
                    name: settings.name,
                    dayTimezone: settings.dayTimezone,
                    locale: settings.locale,
                    currency: next,
                  })
                  .catch(() => {
                    // Xato `onError` da ko'rsatildi; bu `catch` faqat antd
                    // oynasini yopish uchun — aks holda konsolda
                    // "unhandled rejection" qolardi (`RolesPage` andozasi).
                  }),
            })
          }}
        >
          {t('common.save')}
        </Button>
      </Space.Compact>
    </>
  )
}

interface TenantSettingsFormProps {
  settings: TenantSettings
  /** `settings.update` yo'q bo'lsa — faqat ko'rsatiladi */
  editable: boolean
  onSaved: (saved: TenantSettings) => void
  onError: (text: string) => void
}

function TenantSettingsForm({ settings, editable, onSaved, onError }: TenantSettingsFormProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<TenantSettingsDraft>()
  const [zone, setZone] = useState(settings.dayTimezone)

  // Ro'yxat uzun (~450 zona) va har yorliq uchun `Intl` formatlagichi
  // yasaladi — shuning uchun bir marta hisoblanadi.
  const zoneOptions = useMemo(timeZoneOptions, [])

  // Server bergan til ro'yxatda bo'lmasligi mumkin (masalan "uz-UZ") —
  // u holda tanlov jimgina yo'qolmasin, ro'yxatga qo'shiladi.
  /*
    ⚠️ YORLIQ — TIL NOMI, kod EMAS. `en` degan qiymat tanlagichda
    "sukut til" savoliga javob bermaydi; lug'at (`language.*`) esa
    panelning til almashtirgichida ALLAQACHON ishlatiladi.

    ⚠️ Kod O'ZI ham qoladi (`Inglizcha · en`): saqlanadigan qiymat
    aynan shu va u boshqa joyda (API javobi, hujjat) shu ko'rinishda
    uchraydi — faqat nom qoldirilsa operator ikkisini bog'lay olmasdi.

    ⚠️ Serverdagi NOTANISH til ro'yxatdan tushib qolmasin: u
    `TENANT_LOCALES` ga qo'shiladi va nomi topilmasa kodning O'ZI
    yorliq bo'ladi (`defaultValue`) — aks holda tanlagich bo'sh
    ko'rinardi va saqlash uni jimgina almashtirib yuborardi.
  */
  const localeOptions = useMemo(() => {
    const codes = TENANT_LOCALES.includes(settings.locale as (typeof TENANT_LOCALES)[number])
      ? [...TENANT_LOCALES]
      : [...TENANT_LOCALES, settings.locale]
    return codes.map((code) => {
      const name = t(`language.${code}`, { defaultValue: '' })
      return { value: code, label: name === '' ? code : `${name} · ${code}` }
    })
  }, [settings.locale, t])

  const save = useMutation({
    mutationFn: updateTenantSettings,
    onSuccess: onSaved,
    onError: (error) => onError(translateError(error, t)),
  })

  return (
    <Form<TenantSettingsDraft>
      form={form}
      layout="vertical"
      disabled={!editable || save.isPending}
      initialValues={{
        name: settings.name,
        dayTimezone: settings.dayTimezone,
        locale: settings.locale,
      }}
      onFinish={(values) =>
        save.mutate({
          name: values.name.trim(),
          dayTimezone: values.dayTimezone,
          locale: values.locale,
        })
      }
    >
      {!editable ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('settings.tenant.readOnly')}
        />
      ) : null}

      <Form.Item
        name="name"
        label={t('settings.tenant.name')}
        rules={[{ required: true, message: t('settings.tenant.nameRequired') }]}
      >
        <Input maxLength={120} />
      </Form.Item>

      <Form.Item
        name="dayTimezone"
        label={t('timezone.label')}
        extra={t('settings.tenant.zoneHint')}
        rules={[{ required: true }]}
      >
        <Select
          showSearch
          options={zoneOptions}
          onChange={setZone}
          // Yorliq siljish bilan ("Asia/Kabul · UTC+04:30"), qidiruv esa
          // ikkalasi bo'yicha ham ishlasin
          optionFilterProp="label"
          notFoundContent={t('settings.tenant.zoneNoMatch')}
        />
      </Form.Item>

      {/* Saqlashdan OLDIN ko'rinadigan natija: operator zonani to'g'ri
          tanlaganini bir qarashda tekshiradi */}
      <Statistic
        title={`${t('settings.tenant.zonePreview')} (${zoneOffsetLabel(zone)})`}
        value={formatDateTimeInZone(new Date().toISOString(), zone)}
      />

      <Divider style={{ margin: '12px 0' }} />

      <Form.Item name="locale" label={t('settings.tenant.locale')} extra={t('settings.tenant.localeHint')}>
        <Select options={localeOptions} />
      </Form.Item>

      <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('settings.tenant.code')}>
          <Typography.Text code>{settings.code}</Typography.Text>
        </Descriptions.Item>
        {/*
          ⚠️ LUG'AT CHAQIRILADI, xom kod chizilmaydi. `ISP` va `ACTIVE`
          — bazadagi enum nomlari va ular ekranda hech narsa
          tushuntirmaydi. Tarjimalar ALLAQACHON bor va boshqa sahifada
          ishlatiladi (`TenantsPage`: `businessKind.*`, `tenantState.*`)
          — ya'ni bu yerda ikkinchi nusxa yozish emas, o'shanisini
          chaqirish kerak edi.

          ⚠️ `defaultValue` — MAJBURIY: sxema turlarni yumshoq oladi
          (`z.string().nullish()`) va server bizga noma'lum yangi holat
          yuborishi mumkin. Usiz i18next ekranga `tenantState.FROZEN`
          degan kalitning O'ZINI chizardi — xom koddan ham battar.
        */}
        <Descriptions.Item label={t('settings.tenant.businessKind')}>
          {settings.businessKind === null || settings.businessKind === undefined ? (
            <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
          ) : (
            <Tag>
              {t(`businessKind.${settings.businessKind}`, { defaultValue: settings.businessKind })}
            </Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('settings.tenant.state')}>
          {settings.state === null || settings.state === undefined ? (
            <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
          ) : (
            <Tag color={settings.state === 'ACTIVE' ? 'green' : 'orange'}>
              {t(`tenantState.${settings.state}`, { defaultValue: settings.state })}
            </Tag>
          )}
        </Descriptions.Item>
      </Descriptions>

      <Button type="primary" htmlType="submit" loading={save.isPending} disabled={!editable}>
        {t('common.save')}
      </Button>
    </Form>
  )
}
